'use strict';

// Constants
const MOBILE_BREAKPOINT = 768;

// Module state
let originalStructure = null;
let isMobileLayout = false;
let resizeTimer = null;

// ========================================
// Initialization
// ========================================

function initComparisonTable() {
  const wrapper = getComparisonTableWrapper();
  if ( !wrapper ) return;

  storeOriginalStructure( wrapper );
  handleLayout();
  setupResizeListener();
}

function getComparisonTableWrapper() {
  return document.querySelector( '.e-div--comparision-table-wrapper' );
}

function setupResizeListener() {
  window.addEventListener( 'resize', handleResize );
}

function handleResize() {
  clearTimeout( resizeTimer );
  resizeTimer = setTimeout( handleLayout, 250 );
}

// ========================================
// Structure Management
// ========================================

function storeOriginalStructure( wrapper ) {
  if ( originalStructure ) return;

  const columns = Array.from( wrapper.children );
  if ( columns.length !== 3 ) return;

  originalStructure = {
    wrapper: wrapper,
    columns: columns.map( mapColumnStructure ),
  };
}

function mapColumnStructure( col ) {
  return {
    element: col,
    items: Array.from( col.children ).map( ( child ) => child.cloneNode( true ) ),
  };
}

// ========================================
// Layout Handler
// ========================================

function handleLayout() {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

  if ( isMobile && !isMobileLayout ) {
    convertToMobileLayout();
  } else if ( !isMobile && isMobileLayout ) {
    convertToDesktopLayout();
  } else if ( !isMobile && !isMobileLayout && originalStructure ) {
    // Initial load on desktop - apply span wrapping
    applyDesktopTextWrapping();
  }
}

function applyDesktopTextWrapping() {
  if ( !originalStructure ) return;

  const wrapper = originalStructure.wrapper;
  const paragraphs = wrapper.querySelectorAll( 'p' );

  paragraphs.forEach( ( p ) => {
    const textContent = p.textContent.trim();
    if ( textContent && !p.querySelector( 'span' ) ) {
      p.innerHTML = '';
      const span = document.createElement( 'span' );
      span.textContent = textContent;
      p.appendChild( span );
    }
  } );
}

// ========================================
// Mobile Layout Functions
// ========================================

function convertToMobileLayout() {
  if ( !originalStructure ) return;

  const wrapper = originalStructure.wrapper;
  const columns = originalStructure.columns;

  // Get max number of items in any column
  const maxItems = Math.max( ...columns.map( ( col ) => col.items.length ) );

  // Clear wrapper
  wrapper.innerHTML = '';

  // Create row divs for mobile
  for ( let i = 0; i < maxItems; i++ ) {
    const rowDiv = createMobileRowDiv( i );
    addItemsToMobileRow( rowDiv, columns, i );
    wrapper.appendChild( rowDiv );
  }

  isMobileLayout = true;
}

function createMobileRowDiv( rowIndex ) {
  const rowDiv = document.createElement( 'div' );
  rowDiv.className =
    rowIndex === 0
      ? 'e-div--comparision-header-mobile'
      : 'e-div--comparision-row-mobile';
  return rowDiv;
}

function addItemsToMobileRow( rowDiv, columns, rowIndex ) {
  const columnOrder = getColumnOrder( rowIndex );

  columnOrder.forEach( ( colIndex ) => {
    const item = columns[colIndex]?.items?.[rowIndex];
    if ( item ) {
      const itemClone = item.cloneNode( true );
      appendItemToRow( rowDiv, itemClone, rowIndex );
    }
  } );
}

function getColumnOrder( rowIndex ) {
  // Add items in order: column 2, column 1, column 3
  // But skip column 2 (index 1) on the first row (rowIndex === 0)
  return rowIndex === 0 ? [0, 2] : [1, 0, 2];
}

function appendItemToRow( rowDiv, itemClone, rowIndex ) {
  if ( shouldWrapImage( itemClone, rowIndex ) ) {
    const wrappedElement = wrapImageElement( itemClone );
    rowDiv.appendChild( wrappedElement );
  } else {
    rowDiv.appendChild( itemClone );
  }
}

function shouldWrapImage( itemClone, rowIndex ) {
  return (
    rowIndex === 0 &&
    itemClone.classList?.contains( 'e-img' )
  );
}

function wrapImageElement( itemClone ) {
  const wrapperDiv = document.createElement( 'div' );
  wrapperDiv.className = 'e-img--wrapper';
  wrapperDiv.appendChild( itemClone );
  return wrapperDiv;
}

// ========================================
// Desktop Layout Functions
// ========================================

function convertToDesktopLayout() {
  if ( !originalStructure ) return;

  const wrapper = originalStructure.wrapper;
  const columns = originalStructure.columns;

  // Clear wrapper
  wrapper.innerHTML = '';

  // Restore original column structure
  columns.forEach( ( col, index ) => {
    const columnDiv = createDesktopColumnDiv( col, index );
    populateColumnItems( columnDiv, col.items );
    wrapper.appendChild( columnDiv );
  } );

  isMobileLayout = false;
}

function createDesktopColumnDiv( col, index ) {
  const columnDiv = col.element.cloneNode( false );

  // Add class to first column
  if ( index === 0 ) {
    columnDiv.classList.add( 'e-div--comparision-table-column-one' );
  }

  return columnDiv;
}

function wrapParagraphTextWithSpan( element ) {
  if ( element.tagName === 'P' ) {
    const textContent = element.textContent.trim();
    if ( textContent ) {
      element.innerHTML = '';
      const span = document.createElement( 'span' );
      span.textContent = textContent;
      element.appendChild( span );
    }
  }
  return element;
}

function populateColumnItems( columnDiv, items ) {
  items.forEach( ( item ) => {
    const itemClone = item.cloneNode( true );
    const processedItem = wrapParagraphTextWithSpan( itemClone );
    columnDiv.appendChild( processedItem );
  } );
}

// ========================================
// Bootstrap
// ========================================

if ( document.readyState === 'loading' ) {
  document.addEventListener( 'DOMContentLoaded', initComparisonTable );
} else {
  initComparisonTable();
}
